<template>
  <div ref="containerRef" class="fixed z-30" :style="containerStyle">
    <Popover v-slot="{ open }">
      <!-- Dummy node to notify this component when the Popover opens or closes -->
      <template>
        {{ isOpen(open) }}
      </template>
      <Float
        placement="top-end"
        :offset="15"
        :shift="6"
        :flip="10"
        :z-index="30"
        portal
        enter="transition duration-200 ease-out"
        enter-from="opacity-0 translate-y-1"
        enter-to="opacity-100 translate-y-0"
        leave="transition duration-150 ease-in"
        leave-from="opacity-100 translate-y-0"
        leave-to="opacity-0 translate-y-1"
        :show="localOpen"
      >
        <PopoverButton as="template">
          <ButtonBase
            shape="circular"
            size="xl"
            aria-label="Open BidBoost agent chat"
            :class="[
              'shadow-lg hover:shadow-xl !transition-all !duration-200 touch-none',
              isDragging ? 'cursor-grabbing' : 'cursor-grab',
            ]"
            @pointerdown="startDragging"
            @click.capture="preventClickAfterDrag"
          >
            <SparklesIcon class="h-6 w-6" aria-hidden="true" />
          </ButtonBase>
        </PopoverButton>

        <PopoverPanel
          v-slot="{ close }"
          static
          class="w-96 max-w-[calc(min(100vw-32px,56rem))] shadow-lg focus:outline-none rounded-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5"
        >
          <div class="relative overflow-hidden rounded-lg">
            <AgentChatPanel :open="localOpen" @close="localClose(close)" />
          </div>
        </PopoverPanel>
      </Float>
    </Popover>
  </div>
</template>

<script setup lang="ts">
import ButtonBase from '@/Common/Elements/Buttons/ButtonBase.vue'
import { Float } from '@headlessui-float/vue'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { SparklesIcon } from '@heroicons/vue/20/solid'
import {
  StorageSerializers,
  useEventListener,
  useLocalStorage,
} from '@vueuse/core'
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue'
import AgentChatPanel from './AgentChatPanel.vue'

type PopoverPosition = {
  x: number
  y: number
}

type SavedPopoverPosition = Partial<PopoverPosition> & {
  anchoredRight?: boolean
  anchoredBottom?: boolean
  right?: number
  bottom?: number
}

const storageKey = 'agent_chat_popover_position'
const defaultViewportMargin = 24
const minimumViewportMargin = 8
const dragThreshold = 4

const localOpen = ref<boolean>(false)
const containerRef = ref<HTMLElement | null>(null)
const savedPosition = useLocalStorage<SavedPopoverPosition>(
  storageKey,
  {},
  {
    serializer: StorageSerializers.object,
  }
)
const position = ref<PopoverPosition | null>(null)
const anchoredRight = ref(false)
const anchoredBottom = ref(false)
const rightOffset = ref(defaultViewportMargin)
const bottomOffset = ref(defaultViewportMargin)
const isDragging = ref(false)
const clickShouldBeSuppressed = ref(false)

let cleanupPointerMove: (() => void) | null = null
let cleanupPointerUp: (() => void) | null = null
let dragStart: {
  pointerX: number
  pointerY: number
  positionX: number
  positionY: number
  anchoredRight: boolean
  anchoredBottom: boolean
  rightOffset: number
  bottomOffset: number
} | null = null

const containerStyle = computed(() => {
  if (!position.value) {
    return {
      bottom: `${defaultViewportMargin}px`,
      right: `${defaultViewportMargin}px`,
    }
  }

  return {
    left: `${position.value.x}px`,
    top: `${position.value.y}px`,
  }
})

onMounted(async () => {
  await nextTick()
  initializePosition()
})

useEventListener('resize', () => {
  if (!position.value) return

  updatePositionForViewportResize()
})

function localClose(close: () => void) {
  localOpen.value = false
  close()
}
function isOpen(open: boolean) {
  if (localOpen.value === false && open === true) {
    localOpen.value = true
  }
}

function startDragging(event: PointerEvent) {
  if (event.pointerType === 'mouse' && event.button !== 0) return

  initializePosition()

  if (!position.value) return

  dragStart = {
    pointerX: event.clientX,
    pointerY: event.clientY,
    positionX: position.value.x,
    positionY: position.value.y,
    anchoredRight: anchoredRight.value,
    anchoredBottom: anchoredBottom.value,
    rightOffset: rightOffset.value,
    bottomOffset: bottomOffset.value,
  }

  cleanupPointerMove = useEventListener(window, 'pointermove', drag)
  cleanupPointerUp = useEventListener(window, 'pointerup', stopDragging)
}

function drag(event: PointerEvent) {
  if (!dragStart) return

  const deltaX = event.clientX - dragStart.pointerX
  const deltaY = event.clientY - dragStart.pointerY

  if (!isDragging.value && Math.hypot(deltaX, deltaY) < dragThreshold) {
    return
  }

  isDragging.value = true
  clickShouldBeSuppressed.value = true
  event.preventDefault()

  const nextPosition = {
    x: dragStart.positionX + deltaX,
    y: dragStart.positionY + deltaY,
  }
  const clampedPosition = clampPosition(nextPosition)
  const bounds = getPositionBounds()

  if (dragStart.anchoredRight && Math.abs(deltaX) < dragThreshold) {
    anchoredRight.value = true
    rightOffset.value = dragStart.rightOffset
    clampedPosition.x = clampPosition({
      x: getRightAnchoredX(rightOffset.value),
      y: clampedPosition.y,
    }).x
  } else {
    anchoredRight.value = nextPosition.x >= bounds.maxX
    rightOffset.value = getRightOffset(clampedPosition)
  }

  if (dragStart.anchoredBottom && Math.abs(deltaY) < dragThreshold) {
    anchoredBottom.value = true
    bottomOffset.value = dragStart.bottomOffset
    clampedPosition.y = clampPosition({
      x: clampedPosition.x,
      y: getBottomAnchoredY(bottomOffset.value),
    }).y
  } else {
    anchoredBottom.value = nextPosition.y >= bounds.maxY
    bottomOffset.value = getBottomOffset(clampedPosition)
  }

  position.value = clampedPosition
}

function stopDragging() {
  if (isDragging.value && position.value) {
    saveCurrentPosition()
  }

  dragStart = null
  isDragging.value = false

  if (cleanupPointerMove) cleanupPointerMove()
  if (cleanupPointerUp) cleanupPointerUp()

  cleanupPointerMove = null
  cleanupPointerUp = null

  window.setTimeout(() => {
    clickShouldBeSuppressed.value = false
  })
}

function preventClickAfterDrag(event: MouseEvent) {
  if (!clickShouldBeSuppressed.value) return

  event.preventDefault()
  event.stopImmediatePropagation()
  clickShouldBeSuppressed.value = false
}

function initializePosition() {
  const saved = getSavedPosition()

  if (saved) {
    position.value = saved
    return
  }

  anchoredRight.value = true
  anchoredBottom.value = true
  rightOffset.value = defaultViewportMargin
  bottomOffset.value = defaultViewportMargin
  position.value = clampPosition(getDefaultPosition())
}

function updatePositionForViewportResize() {
  const nextPosition = getPositionForCurrentAnchors()
  const clampedPosition = clampPosition(nextPosition)
  const bounds = getPositionBounds()

  if (!anchoredRight.value && nextPosition.x >= bounds.maxX) {
    anchoredRight.value = true
    rightOffset.value = getRightOffset(clampedPosition)
  }

  if (!anchoredBottom.value && nextPosition.y >= bounds.maxY) {
    anchoredBottom.value = true
    bottomOffset.value = getBottomOffset(clampedPosition)
  }

  position.value = clampedPosition
  saveCurrentPosition()
}

function clampPosition(nextPosition: PopoverPosition): PopoverPosition {
  const { maxX, maxY } = getPositionBounds()

  return {
    x: Math.min(Math.max(nextPosition.x, minimumViewportMargin), maxX),
    y: Math.min(Math.max(nextPosition.y, minimumViewportMargin), maxY),
  }
}

function getPositionBounds() {
  const { width, height } = getContainerDimensions()

  return {
    width,
    height,
    maxX: Math.max(
      minimumViewportMargin,
      window.innerWidth - width - minimumViewportMargin
    ),
    maxY: Math.max(
      minimumViewportMargin,
      window.innerHeight - height - minimumViewportMargin
    ),
  }
}

function getDefaultPosition(): PopoverPosition {
  const { width, height } = getContainerDimensions()

  return {
    x: window.innerWidth - width - defaultViewportMargin,
    y: window.innerHeight - height - defaultViewportMargin,
  }
}

function getPositionForCurrentAnchors(): PopoverPosition {
  return {
    x: anchoredRight.value
      ? getRightAnchoredX(rightOffset.value)
      : (position.value?.x ?? getDefaultPosition().x),
    y: anchoredBottom.value
      ? getBottomAnchoredY(bottomOffset.value)
      : (position.value?.y ?? getDefaultPosition().y),
  }
}

function getRightAnchoredX(offset: number): number {
  const { width } = getContainerDimensions()

  return window.innerWidth - width - offset
}

function getBottomAnchoredY(offset: number): number {
  const { height } = getContainerDimensions()

  return window.innerHeight - height - offset
}

function getRightOffset(nextPosition: PopoverPosition): number {
  const { width } = getContainerDimensions()

  return Math.max(
    minimumViewportMargin,
    window.innerWidth - width - nextPosition.x
  )
}

function getBottomOffset(nextPosition: PopoverPosition): number {
  const { height } = getContainerDimensions()

  return Math.max(
    minimumViewportMargin,
    window.innerHeight - height - nextPosition.y
  )
}

function getContainerDimensions() {
  const rect = containerRef.value?.getBoundingClientRect()

  return {
    width: rect?.width || 56,
    height: rect?.height || 56,
  }
}

function getSavedPosition(): PopoverPosition | null {
  const {
    x,
    y,
    anchoredRight: savedAnchoredRight,
    anchoredBottom: savedAnchoredBottom,
    right,
    bottom,
  } = savedPosition.value ?? {}
  const { width, height } = getContainerDimensions()
  const canAnchorRight =
    savedAnchoredRight === true && typeof right === 'number'
  const canAnchorBottom =
    savedAnchoredBottom === true && typeof bottom === 'number'

  if (typeof x !== 'number' && !canAnchorRight) {
    return null
  }

  if (typeof y !== 'number' && !canAnchorBottom) {
    return null
  }

  const nextPosition = clampPosition({
    x: canAnchorRight ? window.innerWidth - width - right : (x ?? 0),
    y: canAnchorBottom ? window.innerHeight - height - bottom : (y ?? 0),
  })
  const savedRightOffset = getRightOffset(nextPosition)
  const savedBottomOffset = getBottomOffset(nextPosition)

  anchoredRight.value =
    canAnchorRight || savedRightOffset <= defaultViewportMargin
  anchoredBottom.value =
    canAnchorBottom || savedBottomOffset <= defaultViewportMargin
  rightOffset.value = canAnchorRight ? right : savedRightOffset
  bottomOffset.value = canAnchorBottom ? bottom : savedBottomOffset

  return nextPosition
}

function saveCurrentPosition() {
  if (!position.value) return

  savedPosition.value = {
    ...position.value,
    anchoredRight: anchoredRight.value,
    anchoredBottom: anchoredBottom.value,
    right: rightOffset.value,
    bottom: bottomOffset.value,
  }
}

onUnmounted(() => {
  if (cleanupPointerMove) cleanupPointerMove()
  if (cleanupPointerUp) cleanupPointerUp()
})
</script>
